import { useTranslation } from 'react-i18next';
import type { ReportTemplateField } from 'v5/api/report-templates/reportTemplates.api';
import type { ReportFieldOption } from 'v5/core/reporting/reportFieldTree';
import { FormField } from 'v5/design-system/Form';
import { useFieldContext } from 'v5/patterns/forms/schema-form/FieldContext';
import { FieldControlBase } from 'v5/patterns/forms/schema-form/fields/FieldControlBase';
import type { FieldFrameSpec } from 'v5/patterns/forms/schema-form/fields/fieldComponent.types';
import { TableColumnManagerList } from 'v5/patterns/table/TableColumnManagerPopover';
import type { TableColumnManagerField } from 'v5/patterns/table/tableColumnManagerSelection';

/** The server adds them to every file; shown first, never stored. */
const SERVER_COLUMNS = ['id', 'guid'];

export interface ReportColumnsFieldProps {
  spec: FieldFrameSpec;
  /** The template's `fields`: `{ key, name }` in the file's order. */
  path: string;
  options: ReportFieldOption[];
}

/** A report template's columns: the inline column manager over the entity's exportable fields. */
export function ReportColumnsField({
  spec,
  path,
  options
}: ReportColumnsFieldProps) {
  const { t } = useTranslation('reporting', {
    keyPrefix: 'ReportTemplateEditor'
  });
  const readOnly = useFieldContext().mode === 'display';
  const optionsByKey = new Map(options.map(option => [option.key, option]));
  const columnsOf = (
    value: ReportTemplateField[]
  ): TableColumnManagerField[] => [
    { id: 'id', label: t('idColumn'), mandatory: true, pinned: 'start' },
    { id: 'guid', label: t('guidColumn'), mandatory: true, pinned: 'start' },
    ...options.map(option => ({ id: option.key, label: option.label })),
    // a stored column the catalog no longer offers (an archived field) stays until removed
    ...value
      .filter(
        column =>
          !optionsByKey.has(column.key) && !SERVER_COLUMNS.includes(column.key)
      )
      .map(column => ({ id: column.key, label: column.name ?? column.key }))
  ];

  return (
    <FormField
      name={path}
      render={({ field }) => {
        const value: ReportTemplateField[] = field.value;
        const columns = columnsOf(value);
        const keys = [
          ...SERVER_COLUMNS,
          ...value
            .map(column => column.key)
            .filter(key => !SERVER_COLUMNS.includes(key))
        ];
        const labelOf = (key: string) =>
          columns.find(column => column.id === key)?.label ?? key;
        return (
          <FieldControlBase spec={spec}>
            {readOnly ? (
              <ol className="flex flex-col gap-1 rounded-md border border-border-default px-3 py-2 text-base">
                {keys.map(key => (
                  <li key={key}>{labelOf(key)}</li>
                ))}
              </ol>
            ) : (
              <TableColumnManagerList
                className="rounded-md border border-border-default"
                fields={columns}
                value={keys}
                onChange={next =>
                  field.onChange(
                    next
                      .filter(key => !SERVER_COLUMNS.includes(key))
                      .map(
                        key =>
                          value.find(column => column.key === key) ?? {
                            key,
                            name: optionsByKey.get(key)?.name ?? key
                          }
                      )
                  )
                }
              />
            )}
          </FieldControlBase>
        );
      }}
    />
  );
}
